import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, Send, LogOut, FileText, Wallet, DollarSign, AlertCircle, Upload, Download, Trash2, FolderOpen, CheckCircle2, ListChecks, CalendarDays } from 'lucide-react';
import { applicantPortal, applicantPortalTasks } from '@/lib/api/applicant_portal';
import { toast } from 'sonner';

const money = (v) => `A$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ApplicantLoginPage() {
  const [email, setEmail] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (applicantPortal.getToken()) navigate('/applicant/dashboard', { replace: true });
  }, [navigate]);

  React.useEffect(() => {
    const token = params.get('token');
    if (!token) return;
    applicantPortal.consume(token).then((r) => {
      applicantPortal.setToken(r.session_token);
      toast.success(`Welcome, ${r.applicant.name || r.applicant.email}`);
      navigate('/applicant/dashboard', { replace: true });
    }).catch((e) => toast.error(e?.response?.data?.detail || 'Invalid or expired link'));
  }, [params, navigate]);

  const submit = async () => {
    if (!email.trim()) { toast.error('Enter your email'); return; }
    setSending(true);
    try { await applicantPortal.requestLogin(email.trim()); setSent(true); }
    catch { toast.error('Something went wrong'); }
    finally { setSending(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-8" data-testid="applicant-login-page">
      <Card className="w-full max-w-md">
        <CardContent className="p-10">
          <div className="mb-6 text-center">
            <div className="mb-2 font-serif text-2xl font-bold text-[#1A3A5C]">ImmiPro Applicant Portal</div>
            <p className="text-sm text-slate-500">View all your matters, invoices and DHA requests in one place.</p>
          </div>
          {sent ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center text-sm text-emerald-800" data-testid="applicant-login-sent">
              <Send className="mx-auto mb-2 h-6 w-6" />
              If <span className="font-semibold">{email}</span> is on file, a sign-in link is on its way.
              <p className="mt-2 text-xs">Check your inbox to continue.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="applicant-login-email" />
              </div>
              <Button className="w-full" onClick={submit} disabled={sending} data-testid="applicant-login-submit">
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}Email me a sign-in link
              </Button>
              <p className="text-center text-xs text-slate-400">Are you a referrer? <a className="underline" href="/referrer/login">Sign in here</a></p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function ApplicantDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!applicantPortal.getToken()) { navigate('/applicant/login', { replace: true }); return; }
    applicantPortal.dashboard()
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 401) { applicantPortal.clearToken(); navigate('/applicant/login', { replace: true }); }
        else toast.error('Failed to load dashboard');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const signOut = () => { applicantPortal.clearToken(); navigate('/applicant/login', { replace: true }); };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-[#2ECC9A]" /></div>;
  if (!data) return null;
  const s = data.summary;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="applicant-dashboard">
      <header className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div>
            <div className="font-serif text-xl font-bold text-[#1A3A5C]">Applicant portal</div>
            <div className="text-xs text-slate-500">Welcome, {data.applicant.name || data.applicant.email}</div>
          </div>
          <Button variant="outline" size="sm" onClick={signOut} data-testid="applicant-signout"><LogOut className="mr-2 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl p-8">
        <div className="grid gap-4 md:grid-cols-4">
          <Kpi icon={FileText} label="Matters" value={s.matter_count} sub="All visa journeys" testid="applicant-kpi-matters" />
          <Kpi icon={Wallet} label="Trust balance" value={money(s.trust_balance)} sub="Held on your behalf" testid="applicant-kpi-trust" />
          <Kpi icon={DollarSign} label="Outstanding" value={money(s.total_outstanding)} sub={`${money(s.total_paid)} paid`} testid="applicant-kpi-outstanding" />
          <Kpi icon={AlertCircle} label="Open DHA requests" value={s.open_dha_requests} sub="Awaiting action" testid="applicant-kpi-dha" />
        </div>

        <Card className="mt-6"><CardContent className="p-6">
          <div className="mb-3 font-serif text-lg font-semibold text-[#1A3A5C]">Your matters</div>
          {data.matters.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">No matters on file yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2">Matter</th><th className="px-3 py-2">Subclass</th><th className="px-3 py-2">Stage</th><th className="px-3 py-2">Trust</th><th className="px-3 py-2">Created</th></tr>
              </thead>
              <tbody>
                {data.matters.map((m) => (
                  <tr key={m.id} className="border-b border-slate-50" data-testid={`applicant-matter-${m.id}`}>
                    <td className="px-3 py-2 font-semibold text-[#2ECC9A]">{m.ref}</td>
                    <td className="px-3 py-2 text-slate-600">{m.subclass || '—'}</td>
                    <td className="px-3 py-2"><Badge className="border border-slate-200 bg-slate-50 text-slate-700">{m.stage || '—'}</Badge></td>
                    <td className="px-3 py-2 text-slate-700">{money(m.trust_balance)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString('en-AU') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent></Card>

        <ApplicantTaskList />

        {data.dha_requests?.length > 0 && (
          <Card className="mt-6"><CardContent className="p-6">
            <div className="mb-3 font-serif text-lg font-semibold text-[#1A3A5C]">Open DHA requests</div>
            <div className="space-y-2">
              {data.dha_requests.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid={`applicant-dha-${d.id}`}>
                  <div>
                    <div className="font-semibold text-[#1A3A5C]">{d.title || d.kind}</div>
                    <div className="text-xs text-slate-500">Matter {d.matter_ref} · {d.kind}{d.due_date ? ` · due ${d.due_date}` : ''}</div>
                  </div>
                  <Badge className="border border-amber-300 bg-amber-100 text-amber-800">{d.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent></Card>
        )}

        {data.invoices?.length > 0 && (
          <Card className="mt-6"><CardContent className="p-6">
            <div className="mb-3 font-serif text-lg font-semibold text-[#1A3A5C]">Invoices</div>
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2">Matter</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Date</th></tr>
              </thead>
              <tbody>
                {data.invoices.slice(0, 30).map((i) => (
                  <tr key={i.id} className="border-b border-slate-50" data-testid={`applicant-invoice-${i.id}`}>
                    <td className="px-3 py-2 font-semibold text-[#2ECC9A]">{i.matter_ref}</td>
                    <td className="px-3 py-2 text-slate-700">{i.description || '—'}</td>
                    <td className="px-3 py-2 font-semibold text-[#1A3A5C]">{money(i.amount)}</td>
                    <td className="px-3 py-2"><Badge className={`border ${i.status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>{i.status}</Badge></td>
                    <td className="px-3 py-2 text-xs text-slate-500">{(i.paid_at || i.created_at || '').slice(0, 10) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        )}

        <ApplicantDocVault matters={data.matters} />
      </main>
    </div>
  );
}

function ApplicantTaskList() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const r = await applicantPortalTasks.list(); setItems(r.items || []); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const complete = async (t) => {
    setBusy(t.id);
    try { await applicantPortalTasks.complete(t.id); toast.success('Nicely done'); await load(); }
    catch { toast.error('Could not mark done'); }
    finally { setBusy(null); }
  };

  if (loading) return null;
  if (!items.length) return null;
  const pending = items.filter((t) => t.status !== 'done');

  return (
    <Card className="mt-6 border-2 border-[#2ECC9A]/30 bg-gradient-to-br from-[#2ECC9A]/5 via-white to-white" data-testid="applicant-task-list">
      <CardContent className="p-6">
        <div className="mb-3 flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-[#2ECC9A]" />
          <div className="font-serif text-lg font-semibold text-[#1A3A5C]">Your to-do list</div>
          <span className="ml-auto rounded-full border border-[#2ECC9A]/30 bg-[#2ECC9A]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1A3A5C]">
            {pending.length} open
          </span>
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Items your migration agent has assigned to you. Tick them off as you complete each one.
        </p>
        <div className="space-y-2">
          {items.map((t) => {
            const isDone = t.status === 'done';
            const overdue = t.due_date && !isDone && new Date(t.due_date) < new Date(new Date().toDateString());
            return (
              <div key={t.id} className={`flex items-start gap-3 rounded-lg border p-3 transition-all ${
                isDone ? 'border-emerald-200 bg-emerald-50/60 opacity-75'
                : overdue ? 'border-red-300 bg-red-50/40'
                : 'border-slate-200 bg-white'}`} data-testid={`applicant-task-row-${t.id}`}>
                <button type="button" onClick={() => !isDone && complete(t)}
                        disabled={isDone || busy === t.id}
                        data-testid={`applicant-task-complete-${t.id}`}
                        title={isDone ? 'Completed' : 'Mark done'}>
                  {busy === t.id
                    ? <Loader2 className="h-5 w-5 animate-spin text-[#2ECC9A]" />
                    : <CheckCircle2 className={`h-5 w-5 ${isDone ? 'text-emerald-500' : 'text-slate-300 hover:text-[#2ECC9A]'} transition-colors`} />}
                </button>
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${isDone ? 'line-through text-slate-500' : 'text-[#1A3A5C]'}`}>
                    {t.title}
                  </div>
                  {t.description && <div className="text-xs text-slate-600">{t.description}</div>}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                    {t.matter_ref && <span className="font-mono text-[#2ECC9A]">Matter {t.matter_ref}</span>}
                    {t.due_date && (
                      <span className={overdue ? 'text-red-600 font-semibold' : ''}>
                        <CalendarDays className="mr-0.5 inline h-3 w-3" /> Due {t.due_date}{overdue ? ' · overdue' : ''}
                      </span>
                    )}
                    {t.requires_upload && (
                      <Badge className="border-[#2ECC9A]/40 bg-[#2ECC9A]/10 text-[#1A3A5C]">
                        <Upload className="mr-0.5 h-2.5 w-2.5" /> upload needed
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function ApplicantDocVault({ matters }) {
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [matterRef, setMatterRef] = React.useState('');
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const r = await applicantPortal.listDocs(); setDocs(r.items || []); }
    catch { toast.error('Could not load your files'); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await applicantPortal.uploadDoc(file, label || null, matterRef || null);
      toast.success(`Uploaded ${file.name}`);
      setLabel(''); setMatterRef('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.file_name}?`)) return;
    try { await applicantPortal.deleteDoc(d.id); toast.success('Deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  const download = async (d) => {
    try {
      const t = applicantPortal.getToken();
      const r = await fetch(applicantPortal.downloadUrl(d.id), { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error('bad');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = d.file_name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error('Download failed'); }
  };

  return (
    <Card className="mt-6" data-testid="applicant-doc-vault"><CardContent className="p-6">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-serif text-lg font-semibold text-[#1A3A5C]">
          <FolderOpen className="h-5 w-5 text-[#2ECC9A]" /> Your document vault
        </div>
        <div className="text-xs text-slate-500">Passport, evidence, everything your paralegal needs</div>
      </div>

      <div className="mb-4 rounded-lg border border-dashed border-slate-200 bg-slate-50/40 p-4">
        <div className="mb-2 grid gap-2 sm:grid-cols-2">
          <input
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
            placeholder="Label (e.g. Passport bio page)"
            value={label} onChange={(e) => setLabel(e.target.value)}
            data-testid="applicant-doc-label"
          />
          <select
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
            value={matterRef} onChange={(e) => setMatterRef(e.target.value)}
            data-testid="applicant-doc-matter"
          >
            <option value="">General (any matter)</option>
            {(matters || []).map((m) => <option key={m.id} value={m.ref}>{m.ref} · {m.subclass || '—'}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" onChange={(e) => upload(e.target.files?.[0])}
                 data-testid="applicant-doc-file"
                 className="block max-w-[300px] text-xs file:mr-2 file:rounded-md file:border-0 file:bg-[#1A3A5C] file:px-3 file:py-1.5 file:text-white hover:file:bg-[#1A3A5C]/90" />
          {uploading && <Loader2 className="h-4 w-4 animate-spin text-[#2ECC9A]" />}
          <span className="text-[10px] text-slate-500">Max 20 MB · PDF/image/doc types</span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#2ECC9A]" /></div>
      ) : docs.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-4">No files uploaded yet.</div>
      ) : (
        <div className="space-y-2" data-testid="applicant-doc-list">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3 text-sm" data-testid={`applicant-doc-${d.id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-[#1A3A5C]">{d.file_name}</div>
                <div className="text-xs text-slate-500">
                  {d.label && `${d.label} · `}
                  {d.matter_ref && `Matter ${d.matter_ref} · `}
                  {(d.size_bytes / 1024).toFixed(1)} KB · {new Date(d.uploaded_at).toLocaleString('en-AU')}
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => download(d)} data-testid={`applicant-doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(d)} data-testid={`applicant-doc-delete-${d.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent></Card>
  );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card data-testid={testid}><CardContent className="p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Icon className="h-4 w-4" />{label}</div>
      <div className="mt-2 font-serif text-2xl font-bold text-[#1A3A5C]">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </CardContent></Card>
  );
}
